import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ModelDao } from '@rock/db-admin';
import type { CoverageRange, EquipmentTypeFamily, Model } from '@rock/types';
import { equipmentTypeFamiliesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import {
    MIN_COVERAGE_PREFIX_LENGTH,
    coverageRangeFromEndpoints,
    coverageRangeKey,
    coverageSpecificity,
    formatCoverageRange,
    normalizeCoveragePrefix,
} from '@rock/manuals';
import { familyOf, typeAffinity } from '@rock/util';
import { Plus, X } from 'lucide-react';
import { type FC, useEffect, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    type CoverageDraft,
    type CoverageSuggestion,
    type DocumentScope,
    checkCoveragePrefix,
    checkCoverageRange,
    coverageDraftActive,
    suggestionKey,
} from './coverageDraft';

const modelDao = new ModelDao();

/** The Coverage field's tooltip — the whole explanation, out of the form's vertical budget. */
export const COVERAGE_HINT =
    'Every current AND future model of a listed brand whose type is listed (or in a listed type’s family) and whose number starts with one of these prefixes — or falls in one of these ranges (KSM45–KSM200 covers KSM150, not KSM3316) — gets this document automatically. Existing models are backfilled silently; new models pick it up (and skip the manual hunt) as they appear. Leave empty to publish for this model only.';

/**
 * The model a PUBLISH is happening on, and the opt-out for linking it. Present only on publish forms —
 * "Edit publication" changes a rule without linking anything, so it has no such model.
 *
 * `covered` is decided by the CALLER (`coverageDraftCoversModel`) rather than here, so the one answer the
 * whole form depends on — does this publish link the model under review? — is computed once and read by
 * both this field and the Notifications row.
 */
export interface CoveragePublishTarget {
    /** How to name the model to the reviewer. */
    modelDisplay: string;
    /** Whether the rule on screen already reaches this model; when true the opt-out isn't offered at all. */
    covered: boolean;
    /** The opt-out's controlled value. Only consulted while `covered` is false. */
    linkThisModel: boolean;
    onLinkThisModelChange: (next: boolean) => void;
}

/** Whether a publish carrying this target will link the model under review. */
export function publishLinksTarget(target: CoveragePublishTarget): boolean {
    return target.covered || target.linkThisModel;
}

/**
 * The Coverage field of the publish/edit form: confirm that a document applies to whole CLASSES of model
 * numbers (prefixes, suggested from the verifier's `coversModels`), and preview the existing models the
 * rule would match under the document's brands and types — the human's only look at its blast radius
 * before saving. Empty prefixes mean the document is published for this model alone.
 *
 * On a publish (`publishTarget`), it also carries the opt-out for linking the model under review — the
 * one control that lets a reviewer file a manual for a DIFFERENT family without serving it here.
 *
 * Renders as bare rows (no box, no heading): it is one field among the form's others, and the prose that
 * used to sit above it lives in {@link COVERAGE_HINT}.
 */
export const CoverageFields: FC<{
    value: CoverageDraft;
    onChange: (next: CoverageDraft) => void;
    /** The brands and types on screen in the form's scope rows — what the preview matches under. */
    scope: DocumentScope;
    saving: boolean;
    /**
     * Predicates the verifier claims this document covers. They start in `value`; this list is what puts a
     * REMOVED one back within one click, so pruning an over-broad extraction stays reversible.
     */
    suggestions?: CoverageSuggestion[];
    /** Enables the "also link this model" opt-out. Omit on forms that aren't publishing. */
    publishTarget?: CoveragePublishTarget;
    /**
     * Reports a predicate typed into the entry row but not yet added, so the form's Save can refuse
     * instead of dropping it. Must be referentially STABLE — it feeds an effect.
     */
    onPendingChange: (pending: string | undefined) => void;
    /** The reviewer hit Save with something pending; the entry row prompts them to resolve it. */
    pendingBlocked: boolean;
}> = ({ value, onChange, scope, saving, suggestions = [], publishTarget, onPendingChange, pendingBlocked }) => {
    // Checking is split from adding (and lives in `coverageDraft`, under test) so the entry row can show
    // the same verdict LIVE, under the field, as the reviewer types — the add-time error and the
    // typed-ahead hint can't drift apart.
    const checkPrefix = (prefix: string) => checkCoveragePrefix(value, prefix);
    const checkRange = (range: CoverageRange) => checkCoverageRange(value, range);

    const addPrefix = (raw: string): string | undefined => {
        const prefix = normalizeCoveragePrefix(raw);
        // Not in `checkPrefix`: every prefix is too short on its way to being long enough, so live-showing
        // this would scold the reviewer for typing. It only matters once they try to add.
        if (prefix.length < MIN_COVERAGE_PREFIX_LENGTH) {
            return `Prefixes need at least ${MIN_COVERAGE_PREFIX_LENGTH} characters`;
        }
        const error = checkPrefix(prefix);
        if (error) return error;
        if (!value.prefixes.includes(prefix)) onChange({ ...value, prefixes: [...value.prefixes, prefix] });
        return undefined;
    };

    const addRange = (range: CoverageRange): string | undefined => {
        const error = checkRange(range);
        if (error) return error;
        if (!value.ranges.some((r) => coverageRangeKey(r) === coverageRangeKey(range))) {
            onChange({ ...value, ranges: [...value.ranges, range] });
        }
        return undefined;
    };

    // A rejected suggestion chip has no field to hang an error under, so this one path still toasts.
    const restore = (suggestion: CoverageSuggestion) => {
        const error = suggestion.kind === 'range' ? addRange(suggestion.range) : addPrefix(suggestion.prefix);
        if (error) toast.error(error);
    };

    const taken = new Set([...value.prefixes, ...value.ranges.map(coverageRangeKey)]);
    const unusedSuggestions = suggestions.filter((suggestion) => !taken.has(suggestionKey(suggestion)));

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
                {value.prefixes.map((prefix) => (
                    <PredicateChip
                        key={prefix}
                        label={prefix}
                        kind="prefix"
                        saving={saving}
                        onRemove={() => onChange({ ...value, prefixes: value.prefixes.filter((p) => p !== prefix) })}
                    />
                ))}
                {value.ranges.map((range) => (
                    <PredicateChip
                        key={coverageRangeKey(range)}
                        label={formatCoverageRange(range)}
                        kind="range"
                        saving={saving}
                        onRemove={() =>
                            onChange({
                                ...value,
                                ranges: value.ranges.filter((r) => coverageRangeKey(r) !== coverageRangeKey(range)),
                            })
                        }
                    />
                ))}
            </div>
            <PredicateEntry
                saving={saving}
                add={{ addPrefix, addRange }}
                check={{ checkPrefix, checkRange }}
                onPendingChange={onPendingChange}
                blocked={pendingBlocked}
            />
            {unusedSuggestions.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Removed:</span>
                    {unusedSuggestions.map((suggestion) => (
                        <button
                            key={suggestion.label}
                            type="button"
                            onClick={() => restore(suggestion)}
                            disabled={saving}
                            className="inline-flex items-center gap-1 rounded-full border border-dashed px-2.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                        >
                            <Plus className="h-3 w-3" />
                            {suggestion.label}
                        </button>
                    ))}
                </div>
            )}
            {coverageDraftActive(value) && (
                <>
                    <MatchingModelsPreview scope={scope} prefixes={value.prefixes} ranges={value.ranges} />
                    {publishTarget && !publishTarget.covered && (
                        <LinkThisModelOptOut target={publishTarget} saving={saving} />
                    )}
                </>
            )}
        </div>
    );
};

/**
 * The escape hatch for "this manual belongs to a DIFFERENT model": publishing always linked the model
 * under review, so a reviewer who found e.g. a 25HCB6 install guide while working a 25HPB6 model had no
 * way to file it where it belongs without also serving it here.
 *
 * Shown only when the rule on screen provably EXCLUDES this model, and unchecked by default — at that
 * point the reviewer has already stated the document doesn't apply here, so linking it would be the
 * surprising outcome. The copy names the notification consequence because it is otherwise invisible:
 * coverage backfill links silently by design, so an unlinked publish announces nothing to anyone.
 * Unchecking is safe precisely because the rule is what carries the document to the models that do want it; a rule matching nothing today still catches future models (and the preview
 * above says so in amber).
 */
const LinkThisModelOptOut: FC<{ target: CoveragePublishTarget; saving: boolean }> = ({ target, saving }) => {
    // Per-instance so two candidate rows with confirm forms open can't share a DOM id — clicking one
    // label would otherwise toggle the other's checkbox.
    const checkboxId = useId();
    return (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2">
            <Checkbox
                id={checkboxId}
                checked={target.linkThisModel}
                onCheckedChange={(next) => target.onLinkThisModelChange(next === true)}
                disabled={saving}
                className="mt-0.5"
            />
            <Label htmlFor={checkboxId} className="cursor-pointer text-xs font-normal leading-relaxed">
                Also link to <span className="font-medium">{target.modelDisplay}</span> — the model you’re reviewing,
                which this rule does <span className="font-medium">not</span> cover. Leave unchecked to file the
                document under its own models only; either way, owners of covered models are not notified.
            </Label>
        </div>
    );
};

/**
 * The add-a-predicate row: an explicit Prefix/Range mode, so nothing about a typed value has to be
 * INFERRED. Prefix mode takes one family prefix; range mode takes the two endpoints AS PRINTED. Both show
 * what they would add live under the fields — the reviewer confirms the normalization (`fv-4` → `FV4`)
 * and the stem split (`KSM` out of `KSM45`) before it becomes a chip, and sees the reason immediately
 * when a value can't be indexed.
 *
 * Typing a predicate and never pressing Add is the row's standing trap: the value looks entered, but only
 * chips are the rule. So the pending value is reported UP (`onPendingChange`) for the form's Save to
 * refuse on, and this row shows the resolve-it prompt when it does. Two ways out, both one click — no
 * silent commit of a value the reviewer never confirmed (coverage reaches every future model of a family,
 * so guessing at their intent is the one thing this field must not do), and no dead Save button either.
 */
const PredicateEntry: FC<{
    saving: boolean;
    add: {
        addPrefix: (raw: string) => string | undefined;
        addRange: (range: CoverageRange) => string | undefined;
    };
    check: {
        checkPrefix: (prefix: string) => string | undefined;
        checkRange: (range: CoverageRange) => string | undefined;
    };
    onPendingChange: (pending: string | undefined) => void;
    blocked: boolean;
}> = ({ saving, add, check, onPendingChange, blocked }) => {
    const [mode, setMode] = useState<'prefix' | 'range'>('prefix');
    const [prefixInput, setPrefixInput] = useState('');
    const [fromInput, setFromInput] = useState('');
    const [toInput, setToInput] = useState('');
    // Add-time errors only. Everything checkable while typing renders from the live derivation instead.
    const [error, setError] = useState<string | undefined>(undefined);
    // Per-instance radio group: candidate rows each render their own confirm form, and a document-wide
    // group would let arrow-keying in one form switch — and so DISCARD — another's typed predicate.
    const modeGroup = useId();

    // Live derivation of whatever is typed so far — the preview line below the fields.
    const derived = useMemo(
        () => (fromInput.trim() && toInput.trim() ? coverageRangeFromEndpoints(fromInput, toInput) : undefined),
        [fromInput, toInput],
    );
    const normalizedPrefix = normalizeCoveragePrefix(prefixInput);

    const pending = mode === 'prefix' ? normalizedPrefix || undefined : describeRangeEntry(fromInput, toInput);

    useEffect(() => {
        onPendingChange(pending);
    }, [pending, onPendingChange]);

    // Unmount ONLY — kept out of the effect above because that one's cleanup fires between updates too,
    // and a momentary "nothing pending" on every keystroke would dismiss the Save prompt mid-read.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the reset must not re-run on identity churn
    useEffect(() => () => onPendingChange(undefined), []);

    const submitPrefix = () => {
        const failure = add.addPrefix(prefixInput);
        setError(failure);
        if (!failure) setPrefixInput('');
    };

    const submitRange = () => {
        if (!derived) {
            setError('Enter both endpoints (e.g. KSM45 and KSM200)');
            return;
        }
        if (!derived.ok) {
            setError(derived.error);
            return;
        }
        const failure = add.addRange(derived.range);
        setError(failure);
        if (failure) return;
        setFromInput('');
        setToInput('');
    };

    const submit = mode === 'prefix' ? submitPrefix : submitRange;

    const discard = () => {
        setPrefixInput('');
        setFromInput('');
        setToInput('');
        setError(undefined);
    };

    // Switching modes drops the other one's half-typed value with it: text the reviewer can't see is
    // exactly the trap this row guards against, and it can't be warned about honestly.
    const changeMode = (next: 'prefix' | 'range') => {
        discard();
        setMode(next);
    };

    const onEnter = (event: { key: string; preventDefault: () => void }) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        submit();
    };

    return (
        <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2 text-xs">
                    {(['prefix', 'range'] as const).map((option) => (
                        <label key={option} className="flex cursor-pointer items-center gap-1 capitalize">
                            <input
                                type="radio"
                                name={modeGroup}
                                checked={mode === option}
                                onChange={() => changeMode(option)}
                                disabled={saving}
                                className="h-3 w-3"
                            />
                            {option}
                        </label>
                    ))}
                </div>
                {mode === 'prefix' ? (
                    <Input
                        value={prefixInput}
                        onChange={(event) => {
                            setPrefixInput(event.target.value);
                            setError(undefined);
                        }}
                        onKeyDown={onEnter}
                        placeholder="e.g. FV4"
                        disabled={saving}
                        autoComplete="off"
                        aria-label="Model-number prefix"
                        className="h-7 w-32"
                    />
                ) : (
                    <div className="flex items-center gap-1.5">
                        <Input
                            value={fromInput}
                            onChange={(event) => {
                                setFromInput(event.target.value);
                                setError(undefined);
                            }}
                            onKeyDown={onEnter}
                            placeholder="KSM45"
                            disabled={saving}
                            autoComplete="off"
                            aria-label="First model in the range"
                            className="h-7 w-28"
                        />
                        <span className="text-xs text-muted-foreground">to</span>
                        <Input
                            value={toInput}
                            onChange={(event) => {
                                setToInput(event.target.value);
                                setError(undefined);
                            }}
                            onKeyDown={onEnter}
                            placeholder="KSM200"
                            disabled={saving}
                            autoComplete="off"
                            aria-label="Last model in the range"
                            className="h-7 w-28"
                        />
                    </div>
                )}
                {/* Solid while something is typed: the button that has work to do shouldn't look identical
                    to the one that doesn't. */}
                <Button
                    type="button"
                    variant={pending ? 'default' : 'outline'}
                    size="sm"
                    onClick={submit}
                    disabled={saving}
                >
                    Add
                </Button>
            </div>
            <EntryHint mode={mode} error={error} derived={derived} prefix={normalizedPrefix} check={check} />
            {blocked && pending && (
                <div
                    role="alert"
                    className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-1.5 text-xs text-amber-800"
                >
                    <span>
                        “<span className="font-medium">{pending}</span>” isn’t added yet — it won’t be part of the rule.
                    </span>
                    <Button type="button" size="sm" className="h-6 px-2 text-xs" onClick={submit} disabled={saving}>
                        Add it
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={discard}
                        disabled={saving}
                    >
                        Discard
                    </Button>
                </div>
            )}
        </div>
    );
};

/**
 * The line under the entry fields: what pressing Add would produce, or why it wouldn't. An add-time
 * `error` wins (it's the newest thing the reviewer did); otherwise the typed value is checked live, so a
 * conflict with an existing chip surfaces before the click rather than after it.
 *
 * Prefix mode deliberately shows its preview from the first character — `→ FV4` is how the reviewer sees
 * that this field normalizes, and that a chip is still one action away.
 */
const EntryHint: FC<{
    mode: 'prefix' | 'range';
    error: string | undefined;
    derived: ReturnType<typeof coverageRangeFromEndpoints> | undefined;
    prefix: string;
    check: {
        checkPrefix: (prefix: string) => string | undefined;
        checkRange: (range: CoverageRange) => string | undefined;
    };
}> = ({ mode, error, derived, prefix, check }) => {
    const [issue, preview] =
        mode === 'prefix'
            ? [
                  prefix.length >= MIN_COVERAGE_PREFIX_LENGTH ? check.checkPrefix(prefix) : undefined,
                  prefix && `→ ${prefix}`,
              ]
            : derived?.ok
              ? [
                    check.checkRange(derived.range),
                    `→ ${formatCoverageRange(derived.range)} — prefix ${derived.range.prefix}, ${derived.range.from}–${derived.range.to}`,
                ]
              : [derived?.error, undefined];

    const problem = error ?? issue;
    const message = problem ?? preview;
    if (!message) return null;
    // Announced when it's a problem: these used to be toasts, which Sonner puts in a live region — a
    // silent inline swap would have left a screen reader with nothing at all where an error had been.
    return (
        <div
            role={problem ? 'alert' : undefined}
            className={cn('text-xs', problem ? 'text-amber-700' : 'text-muted-foreground')}
        >
            {message}
        </div>
    );
};

/** What's typed into the range pair, named POSITIONALLY so a lone endpoint can't read as a prefix. */
function describeRangeEntry(from: string, to: string): string | undefined {
    const [start, end] = [from.trim(), to.trim()];
    if (!start && !end) return undefined;
    return `${start || '…'} to ${end || '…'}`;
}

/** One removable predicate chip — prefixes and ranges dress identically; only the a11y label differs. */
const PredicateChip: FC<{ label: string; kind: 'prefix' | 'range'; saving: boolean; onRemove: () => void }> = ({
    label,
    kind,
    saving,
    onRemove,
}) => (
    <Badge variant="secondary" className="gap-1 border-transparent bg-blue-100 text-blue-800">
        {label}
        <button type="button" aria-label={`Remove ${kind} ${label}`} onClick={onRemove} disabled={saving}>
            <X className="h-3 w-3" />
        </button>
    </Badge>
);

/**
 * Live preview of the EXISTING models a coverage rule matches (brand + stem queries for each listed
 * brand, then the range and type tiers applied client-side, mirroring the server's application). A model
 * reached only through its type's family is counted and named as such, since it links flagged. Type
 * near-misses are shown too — seeing "matches the prefix but not a listed type" before saving is how a
 * too-narrow rule gets caught — as are stem-mates a range EXCLUDES, the human's confirmation the boundary
 * lands where the cover page says. Future models are the other half of the value and can't be previewed
 * by definition.
 */
const MatchingModelsPreview: FC<{
    scope: DocumentScope;
    prefixes: string[];
    ranges: CoverageRange[];
}> = ({ scope, prefixes, ranges }) => {
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const [models, setModels] = useState<Model[] | 'error' | undefined>(undefined);
    // Every key the rule occupies: each brand × prefixes and range STEMS alike (a range over-fetches its
    // stem and is filtered numerically below, exactly as the backfill sweep does). Joined into strings so
    // the effect's deps are honest without refetching on every fresh array identity.
    const stemKey = useMemo(
        () => [...new Set([...prefixes, ...ranges.map((range) => range.prefix)])].sort().join(','),
        [prefixes, ranges],
    );
    const brandKey = [...scope.brandIds].sort().join(',');

    useEffect(() => {
        const stems = stemKey ? stemKey.split(',') : [];
        const brandIds = brandKey ? (brandKey.split(',') as DocumentScope['brandIds']) : [];
        if (stems.length === 0 || brandIds.length === 0) {
            setModels([]);
            return;
        }
        // Back to "Checking…" while the new rule's matches load — a stale count next to fresh chips
        // reads as the new rule's blast radius.
        setModels(undefined);
        let cancelled = false;
        void (async () => {
            try {
                const byId = new Map<Model['id'], Model>();
                for (const brandId of brandIds) {
                    for (const stem of stems) {
                        for (const model of await modelDao.findModelsByBrandAndPrefix(brandId, stem)) {
                            if (!model.archivedAt) byId.set(model.id, model);
                        }
                    }
                }
                if (!cancelled) setModels([...byId.values()]);
            } catch (error) {
                if (cancelled) return; // a superseded fetch's failure is nobody's news
                // A broken preview must say so, never masquerade as "matches 0" — the preview is the
                // human's only look at the rule's blast radius.
                setModels('error');
                toast.error('Coverage preview failed', {
                    description: error instanceof Error ? error.message : `${error}`,
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [brandKey, stemKey]);

    if (scope.brandIds.length === 0 || scope.typeIds.length === 0) {
        return <div className="field-hint">Add the document’s brand and type to preview the models it reaches.</div>;
    }
    if (models === undefined) return <div className="field-hint">Checking existing models…</div>;
    if (models === 'error') {
        return (
            <div className="text-xs text-destructive">Preview unavailable — existing-model matches could not load.</div>
        );
    }
    const matched = models.filter((model) => coverageSpecificity({ prefixes, ranges }, model.modelCanonical) > 0);
    const affinityOf = (model: Model) => typeAffinity(model.typeId, scope.typeIds, families);
    const covered = matched.filter((model) => !!affinityOf(model));
    const viaFamily = covered.filter((model) => affinityOf(model) === 'family');
    const nearMisses = matched.filter((model) => !affinityOf(model));
    const outsideRange = ranges.length > 0 ? models.filter((model) => !matched.includes(model)) : [];
    // Matching nothing is legal (a rule can be purely forward-looking) but it is far more often a typo or
    // a mangled extraction, and the rule is INERT either way — so it reads as a caution, not as prose the
    // eye slides over. Same amber the near-miss line already uses for "look at this before you save".
    if (covered.length === 0) {
        return (
            <div className="space-y-1 text-xs text-muted-foreground">
                <div className="text-amber-700">
                    Matches <span className="font-medium">no existing models</span> — it would apply only to future
                    ones. Check the prefix if that isn't what you meant.
                </div>
                <NearMissLines nearMisses={nearMisses} outsideRange={outsideRange} />
            </div>
        );
    }
    return (
        <div className="space-y-1 text-xs text-muted-foreground">
            <div>
                Matches <span className="font-medium text-foreground">{covered.length}</span> existing model
                {covered.length === 1 ? '' : 's'}
                {': '}
                <span className="text-foreground">{listModelNumbers(covered)}</span>
                {' — future matching models are covered automatically.'}
            </div>
            {viaFamily.length > 0 && <FamilyMatchLine models={viaFamily} families={families} />}
            <NearMissLines nearMisses={nearMisses} outsideRange={outsideRange} />
        </div>
    );
};

/** The covered models that link through a family rather than a listed type — they link flagged. */
const FamilyMatchLine: FC<{ models: Model[]; families: readonly EquipmentTypeFamily[] }> = ({ models, families }) => (
    <div className="text-amber-700">
        {models.length} of them {models.length === 1 ? 'is' : 'are'} reached through a type family, and link flagged:{' '}
        {models
            .slice(0, 5)
            .map((model) => {
                const family = familyOf(model.typeId, families);
                return `${model.modelCanonical} (${model.typeId}${family ? `, ${family.name}` : ''})`;
            })
            .join(', ')}
        {models.length > 5 ? ', …' : ''}
    </div>
);

/**
 * The two "matched, but not covered" footnotes. Shared by both preview branches: a rule that matches
 * nothing is exactly when a near miss is most worth reading — it is usually the reason (right family,
 * wrong type), and hiding it there would strand the reviewer at the least informative moment.
 */
const NearMissLines: FC<{ nearMisses: Model[]; outsideRange: Model[] }> = ({ nearMisses, outsideRange }) => (
    <>
        {nearMisses.length > 0 && (
            <div className="text-amber-700">
                {nearMisses.length} model{nearMisses.length === 1 ? ' matches' : 's match'} the prefix but not a listed
                type:{' '}
                {nearMisses
                    .slice(0, 5)
                    .map((model) => `${model.modelCanonical} (${model.typeId})`)
                    .join(', ')}
                {nearMisses.length > 5 ? ', …' : ''}
            </div>
        )}
        {outsideRange.length > 0 && (
            <div>
                {outsideRange.length} model{outsideRange.length === 1 ? ' shares' : 's share'} a range’s prefix but sit
                {outsideRange.length === 1 ? 's' : ''} outside its bounds (not covered):{' '}
                {listModelNumbers(outsideRange, 5)}
            </div>
        )}
    </>
);

/** The first few model numbers, elided when there are more — the coverage lists' shape. */
export function listModelNumbers(models: Pick<Model, 'modelCanonical'>[], limit = 8): string {
    const shown = models
        .slice(0, limit)
        .map((model) => model.modelCanonical)
        .join(', ');
    return models.length > limit ? `${shown}, …` : shown;
}
